import { msalInstance } from '../../root';

/**
 * Re-mints the B2C token so the `extension_permissions` claim catches up with the database.
 *
 * ADO #7699: the permission rule is evaluated twice — the React gate reads the LIVE database
 * (`GET /Permissions/User`), the API gate reads the `extension_permissions` claim, which the
 * B2C sign-in journey computes ONCE with the same SQL and then freezes into the token. A
 * permission granted after sign-in is therefore visible to the UI but absent from the token,
 * and the prod policy's `SingleSignOn KeepAliveInDays="30"` lets that gap persist for weeks.
 *
 * `acquireTokenSilent({ forceRefresh: true })` alone does NOT close it: redeeming a refresh
 * token re-issues the existing claim values. Only `ssoSilent` re-executes the sign-in user
 * journey — and with it the REST technical profile that recomputes the permission list —
 * against the SSO session cookie, which is why it runs first here.
 *
 * Returns the fresh access token, or `null` when there is no account to refresh. MSAL itself
 * REJECTS when B2C needs interaction (`InteractionRequiredAuthError`) or the SSO session is
 * gone, so callers must handle both — `staleClaimRetry` catches the rejection and treats it
 * exactly like `null`, so a failed refresh can never mask the original 403.
 */
const refreshPermissionsClaim = async (apiScopes: string[]): Promise<string | null> => {
  // ACTIVE account only — no `getAllAccounts()[0]` fallback. root.tsx deliberately leaves the
  // active account null when the cache holds more than one, to force a fresh login; picking an
  // arbitrary account here would silently re-mint a claim for the wrong user. Returning null
  // sends the caller down the "sign out and sign in again" path, which is the right answer.
  const account = msalInstance?.getActiveAccount() ?? null;

  if (!msalInstance || !account) {
    return null;
  }

  await msalInstance.ssoSilent({
    scopes: apiScopes,
    account,
    loginHint: account.username
  });

  const result = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account,
    forceRefresh: true
  });

  return result.accessToken ?? null;
};

export default refreshPermissionsClaim;
